import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { MessageService } from '@services/util/message.service';
import { TokenService } from '@services/helpers/token.service';
import { Observable } from 'rxjs';

/** API payloads vary by endpoint; keep facade returns loose for existing consumers. */
type ApiPayload = any;
import { environment } from 'src/environments/environment';
import { ApiService } from '../services/api/api.service';
import { PrecinctScopeService } from '../services/api/precinct-scope.service';
import { SetConfigurationAction, ResetPDFGenAction } from './actions/configuration-action';
import { SetEntityManagerFormsAction } from './actions/entity-manager-forms-action';
import {
  SetActiveProjectAction,
  SetActiveProjectStatisticsAction,
  SetProjectAction,
  SetProjectInfo,
  SetProjectLockingStatusAction,
} from './actions/project-actions';
import { SetLoggedinUserStatusAction } from './actions/system-user-action';
import { SetContact, SetFields, SetMyNotes, SetMyOffices, SetTagSetting } from './actions/system.action';
import {
  contactInfo,
  getActiveProject,
  getFields,
  getLoggedInUserStatus,
  getMyOffices,
  getTagSetting,
  getUserNotes,
  projectInfo,
  RootReducerState,
} from './reducers';
import { StateCacheFetchService } from './services/state-cache-fetch.service';
import { clearPersistedStore } from './middleware/redux-hydration';
import { isPublicReportLocation } from '@services/helpers/public-report-route';

@Injectable({
  providedIn: 'root',
})
export class ReduxService {
  readonly baseUrl = `${environment.nodeApiUrl}/${environment.api_prefix}`;

  constructor(
    private readonly http: HttpClient,
    private readonly api: ApiService,
    private readonly messageService: MessageService,
    private readonly tokenService: TokenService,
    private readonly localStorageService: LocalStorageService,
    private readonly store: Store<RootReducerState>,
    private readonly cacheFetch: StateCacheFetchService,
    private readonly precinctScope: PrecinctScopeService
  ) {}

  /**
   * True when there's a real login token, OR a still-valid public report viewer token while the
   * browser is on a public report link (`/report-view`, `/report-preview` — see
   * `isPublicReportLocation`). Without the second check, every `fetchTypedBucket`/`fetchOne` call
   * below (getTags, getMyOffices, getUser, …) silently no-ops via `StateCacheFetchService`'s
   * `hasAuth()` gate on a public report session — the viewer token lives in its own storage key
   * (see `TokenService.getPublicReportToken`), not the one `getToken()` reads, so this always read
   * false there even though the request would have been properly authorized (the auth interceptor
   * and `ApiService.shouldProceedWithAuthenticatedRequest` already both check for it). That's what
   * left `RpViewComponent`'s "Lead Status"/"Enquiry Source" tables stuck on "No data" on a public
   * report link: `getTags()` never emitted, so the view widget's own `/get-field-values` fetch
   * (gated behind it) never ran either.
   */
  private hasAuthToken(): boolean {
    if (this.tokenService.getToken()) {
      return true;
    }
    return isPublicReportLocation() && !!this.tokenService.getPublicReportToken();
  }

  getFields(): Observable<ApiPayload> {
    return this.store.pipe(select(getFields));
  }

  setFields(data: ApiPayload): void {
    this.store.dispatch(new SetFields({ data }));
  }

  getTagSetting(): Observable<ApiPayload> {
    return this.store.pipe(select(getTagSetting));
  }

  setTagSetting(data: ApiPayload): void {
    this.store.dispatch(new SetTagSetting({ data }));
  }

  getTags(officeId: string, isRequest = false, type = 'lead', projectId: string | null = null, tagModules = 'tag_modules'): Observable<ApiPayload> {
    const url = projectId
      ? `${this.baseUrl}/general/tag-settings?office_id=${this.api.getOfficeId()}&project_id=${projectId}&type=${tagModules}&module_type=project`
      : `${this.baseUrl}/general/tag-settings?office_id=${officeId}&type=${tagModules}&module_type=${type}`;

    return this.cacheFetch.fetchTypedBucket({
      selector: getTagSetting,
      type,
      force: isRequest,
      // Office tag configuration — the single biggest source of redundant traffic measured in
      // production (five identical 161 KB responses in three minutes; see
      // FRONTEND-DUPLICATE-API-CALLS-2026-09-17.md §3). It cannot change while a user clicks
      // between modules, only when someone edits tags — and every one of those paths calls
      // `invalidateTags()`, so a forced refetch after an edit still reaches the server.
      freshMs: ReduxService.CONFIG_FRESH_MS,
      hasAuth: () => this.hasAuthToken(),
      request: () => this.http.get<ApiPayload>(url),
      persist: bucket => this.setTagSetting(bucket),
    });
  }

  /**
   * How long office-level configuration stays fresh enough to serve a `force` fetch from cache.
   *
   * Five minutes is well beyond a burst of module navigation (which is what produces the duplicate
   * calls) and well short of a working session, so anything that changes on the server without
   * going through this app still appears promptly. Every in-app edit invalidates explicitly rather
   * than waiting this out.
   */
  private static readonly CONFIG_FRESH_MS = 5 * 60 * 1000;

  /**
   * Drops the freshness stamp for tag settings, so the next forced `getTags()` really refetches.
   * Call after ANY tag create/update/delete — that is what makes the freshness window safe.
   * Omit `type` after a change that could affect more than one module's tags.
   */
  invalidateTags(type?: string): void {
    this.cacheFetch.invalidate(getTagSetting, type);
  }

  /** Everything cached is now another office's data (office switch) or nobody's (logout). */
  invalidateAllCaches(): void {
    this.cacheFetch.invalidateAll();
  }

  setUser(data: ApiPayload): void {
    if (data) {
      this.messageService.sendMessage({ userUpdated: true });
    }
    this.store.dispatch(new SetLoggedinUserStatusAction({ data }));
  }

  getUser(isRequest = false): Observable<ApiPayload> {
    return this.cacheFetch.fetchScalar({
      selector: getLoggedInUserStatus,
      force: isRequest,
      hasAuth: () => this.hasAuthToken(),
      isCached: cached => cached != null,
      freshMs: ReduxService.CONFIG_FRESH_MS,
      request: () => this.http.get<ApiPayload>(`${this.baseUrl}/me?include=offices,permissions,groups,teams`),
      persist: response => {
        if (response) {
          this.setUser(response);
        }
      },
    });
  }

  setMyOffices(data: ApiPayload): void {
    this.store.dispatch(new SetMyOffices({ data }));
  }

  getMyOffice(isRequest = false): Observable<ApiPayload> {
    return this.cacheFetch.fetchScalar({
      selector: getMyOffices,
      force: isRequest,
      hasAuth: () => this.hasAuthToken(),
      isCached: cached => cached != null,
      freshMs: ReduxService.CONFIG_FRESH_MS,
      request: () => this.http.get<ApiPayload>(`${this.baseUrl}/offices/my?per_page=all&is_short=true`),
      persist: response => {
        if (response) {
          this.setMyOffices(response);
        }
      },
    });
  }

  setNotes(data: ApiPayload): void {
    this.store.dispatch(new SetMyNotes({ data }));
  }

  getUserNotes(isRequest = false, projectId?: string, page = 1, perPage = 999, keyword = '', type?: string | null): Observable<ApiPayload> {
    return this.cacheFetch.fetchScalar({
      selector: getUserNotes,
      force: isRequest,
      hasAuth: () => this.hasAuthToken(),
      isCached: cached => cached != null,
      request: () => {
        let url = `${this.baseUrl}/user-notes?office_id=${this.api.getOfficeId()}&user_id=${this.api.getUserId()}&page=${page}&per_page=${perPage}`;
        if (projectId) {
          url += `&project_id=${projectId}`;
        }
        if (keyword) {
          url += `&keyword=${encodeURIComponent(keyword)}`;
        }
        // type undefined -> param omitted; null -> "all" (type=null); '' -> "other"; else the type value.
        if (type !== undefined) {
          url += `&type=${type === null ? 'null' : encodeURIComponent(type)}`;
        }
        return this.http.get<ApiPayload>(url);
      },
      persist: response => {
        // Only the default, full (non-paginated, non-filtered, non-project) fetch feeds the
        // shared notes store. Paginated, searched, or project-scoped results are returned to
        // the caller only, so they can't leak into other consumers (e.g. app-notes).
        if (response && !projectId && page === 1 && perPage >= 999 && !keyword && type === undefined) {
          this.setNotes(response);
        }
      },
    });
  }

  getActiveProject(): Observable<ApiPayload> {
    return this.store.select(getActiveProject);
  }

  /**
   * How long a `force`-fetched project/contact info bucket stays fresh enough to skip a refetch.
   *
   * Unlike `CONFIG_FRESH_MS` (tag-settings/me/offices-my), nothing here invalidates on write, so
   * this window is kept short — long enough to collapse the measured burst (several project-edit
   * tabs each re-fetching `internal-<projectId>`/`contacts-filter` seconds apart while a user
   * browses, see FRONTEND-DUPLICATE-API-CALLS-2026-09-17.md §4 patterns #3 and #5), short enough
   * that a change made moments earlier is very unlikely to still be showing once it matters.
   */
  static readonly BURST_FRESH_MS = 30 * 1000;

  getSetProjectInfo(isRequest = false, type = 'lead', url: string, freshMs?: number): Observable<ApiPayload> {
    return this.cacheFetch.fetchTypedBucket({
      selector: projectInfo,
      type,
      force: isRequest,
      freshMs,
      hasAuth: () => this.hasAuthToken(),
      request: () => this.http.get<ApiPayload>(`${this.baseUrl}${url}`),
      persist: bucket => this.store.dispatch(new SetProjectInfo({ data: bucket })),
    });
  }

  setProjectInfo(): void {
    this.store.dispatch(new SetProjectInfo({ data: null }));
  }

  getSetContactInfo(isRequest = false, type: string, url: string, freshMs?: number): Observable<ApiPayload> {
    return this.cacheFetch.fetchTypedBucket({
      selector: contactInfo,
      type,
      force: isRequest,
      freshMs,
      hasAuth: () => this.hasAuthToken(),
      request: () => this.http.get<ApiPayload>(`${this.baseUrl}${url}`),
      persist: bucket => this.store.dispatch(new SetContact({ data: bucket })),
    });
  }

  clearStoreCachesOnLogout(): void {
    this.setUser(null);
    this.setMyOffices(null);
    this.setTagSetting(null);
    this.store.dispatch(new SetFields({ data: null }));
    this.store.dispatch(new SetMyNotes({ data: null }));
    this.store.dispatch(new SetContact({ data: null }));
    this.store.dispatch(new SetProjectInfo({ data: null }));
    this.store.dispatch(new SetProjectAction({ data: [] }));
    this.store.dispatch(new SetActiveProjectAction({ data: null }));
    this.store.dispatch(new SetActiveProjectStatisticsAction({ data: null }));
    this.store.dispatch(
      new SetProjectLockingStatusAction({
        data: { is_locked: false, locked_by: '' },
      })
    );
    this.store.dispatch(new SetEntityManagerFormsAction({ data: [] }));
    this.store.dispatch(new SetConfigurationAction({ data: [] }));
    this.store.dispatch(new ResetPDFGenAction());
    // Precinct scope lives in its own localStorage key, so clearPersistedStore below won't
    // touch it — the next user on this browser would otherwise inherit the previous one's view.
    this.precinctScope.reset();
    // Freshness stamps are in-memory, so clearing the persisted store doesn't touch them. Without
    // this, a logout/office switch that doesn't reload the page would let the next identity be
    // served the previous one's office config from cache for up to CONFIG_FRESH_MS.
    this.cacheFetch.invalidateAll();
    clearPersistedStore(this.localStorageService);
  }
}
